import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TMealPlanItem } from 'interfaces/mealPlan';
import { useSaveMealPlanImage, useUploadMealPlanImage } from 'hooks';
import { showUploadError } from 'apis';
import { modalMethod } from 'components';
import { getImageUploadError, UPLOAD_IMAGE_ACCEPT } from 'utils';

import NutritionModal from './NutritionModal';
import { MealPlanCardWrapper, MealPlanThumbnail, MealPlanButtonGroup } from './styles';

interface TImageData {
  file: File;
  previewURL: string;
}
interface MenuPlanCardProps {
  mealPlanIdx: number;
  mealPlanName: string;
  mealPlanImage: string;
  mealPlanItems: TMealPlanItem[];
  mealPlanContent: string;
  nutritionInfo: {
    totalCalories: number;
    totalCarbohydrate: number;
    totalFat: number;
    totalNatrium: number;
    totalProtein: number;
    totalSugar: number;
  };
}

const MenuPlanCard = ({
  mealPlanIdx,
  mealPlanName,
  mealPlanImage,
  mealPlanItems,
  mealPlanContent,
  nutritionInfo
}: MenuPlanCardProps) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>();
  const [imageInfo, setImageInfo] = useState<TImageData>({ file: null, previewURL: mealPlanImage || '' });
  const [isIngredientModal, setIsIngredientModal] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { mutateAsync: saveMealPlanImage } = useSaveMealPlanImage();
  const uploadMealPlanImage = useUploadMealPlanImage();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging, index, newIndex } = useSortable({
    id: mealPlanIdx
  });

  const imageUploadOnClick = () => {
    if (index === newIndex) {
      inputRef?.current?.click();
    }
  };

  const uploadImage = async (file: File) => {
    if (isUploading) return;
    const validationError = getImageUploadError(file.name);
    if (validationError) {
      modalMethod.warning(validationError);
      return;
    }

    setIsUploading(true);
    try {
      const mealPlanImageUrl = await uploadMealPlanImage(file);
      await saveMealPlanImage({ mealPlanIdx, params: { mealPlanImageUrl } });
    } catch (error) {
      showUploadError(error);
      return;
    } finally {
      setIsUploading(false);
      // 실패 후 같은 파일을 다시 골라도 onChange가 발생하도록
      if (inputRef.current) inputRef.current.value = '';
    }
    setImageInfo({ file, previewURL: URL.createObjectURL(file) });
  };

  const handleImageDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!imageInfo?.previewURL) {
      event.preventDefault();
      const item = event.dataTransfer.items[0];
      const file = item?.getAsFile();
      if (file) uploadImage(file);
    }
  };

  const handleImageChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (!imageInfo?.previewURL) {
      const file = target.files[0];
      if (file) uploadImage(file);
    }
  };

  const buttonMouseDown = (event: React.MouseEvent<Element>) => event.stopPropagation();

  const handleOnOffIngredientModal = () => setIsIngredientModal(!isIngredientModal);

  const handleMenuPlanEdit = () => navigate(`/mealPlan/${mealPlanIdx}`);

  return (
    <Col
      ref={setNodeRef}
      span={6}
      style={{
        minWidth: '430px',
        maxWidth: '430px',
        height: '346px',
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999999 : 1
      }}
    >
      <MealPlanCardWrapper {...listeners} {...attributes}>
        <MealPlanThumbnail role="presentation" onPointerUp={imageUploadOnClick} onDrop={handleImageDrop}>
          {!imageInfo?.previewURL ? (
            <input type="file" accept={UPLOAD_IMAGE_ACCEPT} ref={inputRef} onChange={handleImageChange} />
          ) : null}
          {!imageInfo?.previewURL ? (
            <div className="non-image-description">
              <div>이미지를 등록해주세요</div>
              <div>드래그 앤 드랍 또는 클릭</div>
              <div className="image-guide-description">(이미지 권장 사이즈 400 x 300)</div>
            </div>
          ) : (
            <img alt="previewImage" className="preview-image" draggable={false} src={imageInfo?.previewURL} />
          )}
        </MealPlanThumbnail>
        <div className="menu-plan-name">{mealPlanName}</div>
        <MealPlanButtonGroup>
          <Button type="primary" onClick={handleOnOffIngredientModal} onMouseDown={buttonMouseDown}>
            식단구성
          </Button>
          <Button type="primary" className="edit-button" onClick={handleMenuPlanEdit} onMouseDown={buttonMouseDown}>
            수정하기
          </Button>
        </MealPlanButtonGroup>
      </MealPlanCardWrapper>
      <NutritionModal
        open={isIngredientModal}
        mealPlanContent={mealPlanContent}
        mealPlanItems={mealPlanItems}
        mealPlanName={mealPlanName}
        nutritionInfo={nutritionInfo}
        onCancel={handleOnOffIngredientModal}
      />
    </Col>
  );
};

export default MenuPlanCard;
